/**
 * Simplified Error Handling
 * 
 * Streamlined error handling system with essential error types
 * and simplified interfaces.
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useFeedback } from '@/shared/contexts/FeedbackContext';

// Simplified error types - reduced to essential categories
export enum ErrorType {
  API = 'api',           // All API and network related errors
  AUTH = 'auth',         // Authentication and authorization errors
  VALIDATION = 'validation', // Input validation errors
  SYSTEM = 'system'      // System and unexpected errors
}

// Simplified error interface
export interface AppError {
  type: ErrorType;
  message: string;
  details?: Record<string, any>;
  timestamp: Date;
}

// Simplified error context interface
interface ErrorContextType {
  errors: AppError[];
  addError: (error: Omit<AppError, 'timestamp'>) => void;
  clearErrors: () => void;
  hasErrors: boolean;
}

// Create error context
const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

/**
 * ErrorProvider Component
 * 
 * Global provider for error handling
 */
export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [errors, setErrors] = useState<AppError[]>([]);
  const { toast: showToast } = useFeedback();
  
  // Add a new error
  const addError = useCallback((error: Omit<AppError, 'timestamp'>) => {
    const newError: AppError = {
      ...error,
      timestamp: new Date(),
    };
    
    setErrors((prev) => [...prev, newError]);
    
    // Show toast notification
    showToast({
      title: getErrorTitle(error.type),
      description: error.message,
      type: error.type === ErrorType.VALIDATION ? 'warning' : 'destructive',
    });
  }, [showToast]);
  
  // Clear all errors
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);
  
  // Check if there are any errors
  const hasErrors = errors.length > 0;
  
  // Get error title based on type
  const getErrorTitle = (type: ErrorType): string => {
    switch (type) {
      case ErrorType.API:
        return 'Connection Error';
      case ErrorType.AUTH:
        return 'Authentication Error';
      case ErrorType.VALIDATION:
        return 'Validation Error';
      case ErrorType.SYSTEM:
        return 'System Error';
      default:
        return 'Error';
    }
  };
  
  return (
    <ErrorContext.Provider value={{ errors, addError, clearErrors, hasErrors }}>
      {children}
    </ErrorContext.Provider>
  );
};

/**
 * useError Hook
 * 
 * Hook for using error handling throughout the application
 */
export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  
  return context;
};

/**
 * API Error Handler
 * 
 * Simplified utility function to handle API errors
 */
export const handleApiError = (error: any, addError: ErrorContextType['addError']): AppError => {
  let appError: Omit<AppError, 'timestamp'>;
  
  // Handle different error types with simplified logic
  if (error.response) {
    const status = error.response.status;
    
    if (status === 401 || status === 403) {
      appError = {
        type: ErrorType.AUTH,
        message: status === 401 
          ? 'Your session has expired. Please sign in again.' 
          : 'You do not have permission to perform this action.',
        details: error.response.data,
      };
    } else if (status === 400 || status === 422) {
      appError = {
        type: ErrorType.VALIDATION,
        message: 'Please check your input and try again.',
        details: error.response.data,
      };
    } else {
      appError = {
        type: ErrorType.API,
        message: error.response.data?.message || 'An error occurred while communicating with the server.',
        details: error.response.data,
      };
    }
  } else if (error.request) {
    // Request was made but no response received
    appError = {
      type: ErrorType.API,
      message: 'Unable to connect to the server. Please check your internet connection.',
      details: { request: error.request },
    };
  } else {
    // Error setting up the request or unexpected error
    appError = {
      type: ErrorType.SYSTEM,
      message: error.message || 'An unexpected error occurred.',
      details: { error },
    };
  }
  
  // Add error to context
  addError(appError);
  
  return {
    ...appError,
    timestamp: new Date(),
  };
};

export default {
  ErrorProvider,
  useError,
  handleApiError,
  ErrorType
};
